import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { KeyRound, ArrowRight, AlertCircle, Download, Smartphone, X, Share } from 'lucide-react';
import axios from 'axios';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

var Landing = function() {
  var _useState1 = useState('');
  var code = _useState1[0];
  var setCode = _useState1[1];
  
  var _useState2 = useState(false);
  var loading = _useState2[0];
  var setLoading = _useState2[1];
  
  var _useState3 = useState('');
  var error = _useState3[0];
  var setError = _useState3[1];

  var _useState4 = useState(null);
  var deferredPrompt = _useState4[0];
  var setDeferredPrompt = _useState4[1];

  var _useState5 = useState(false);
  var showInstall = _useState5[0];
  var setShowInstall = _useState5[1];

  var _useState6 = useState(false);
  var showIosModal = _useState6[0];
  var setShowIosModal = _useState6[1];

  var _useState7 = useState(false);
  var showAndroidModal = _useState7[0];
  var setShowAndroidModal = _useState7[1];

  var isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  var isAndroid = /Android/.test(navigator.userAgent);
  var isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  
  var navigate = useNavigate();

  useEffect(function() {
    var handler = function(e) {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return function() { window.removeEventListener('beforeinstallprompt', handler); };
  }, []);

  var handleInstall = async function() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      var result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowInstall(false);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: show manual Android install instructions
      setShowAndroidModal(true);
    }
  };

  var handleSubmit = async function(e) {
    e.preventDefault();
    if (!code.trim()) {
      setError('Por favor, introduce el código de tu empresa');
      return;
    }

    setLoading(true);
    setError('');

    try {
      var response = await axios.post(BACKEND_URL + '/api/verify-code', { code: code.toUpperCase() });
      
      if (response.data.valid) {
        localStorage.setItem('employeeAccess', JSON.stringify({
          company_id: response.data.company_id,
          company_name: response.data.company_name,
          company_logo: response.data.company_logo,
          code: code.toUpperCase(),
          showWelcome: true
        }));
        
        navigate('/ofertas');
      } else {
        setError('Código no válido. Verifica el código proporcionado por tu empresa.');
      }
    } catch (err) {
      console.error('Error verifying code:', err);
      setError('Error al verificar el código. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  var handleCodeChange = function(e) {
    setCode(e.target.value.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_619dfbd9-e9ac-4905-ae17-ad2e8903622d/artifacts/tnur302o_Firma%2020%20Aniversario%20%28251%20x%2088%20px%29%20%28350%20x%20100%20px%29%20%281%29%20%282%29%20%281%29.png" 
                alt="Cámara Lanzarote" 
                className="h-10 w-auto"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_619dfbd9-e9ac-4905-ae17-ad2e8903622d/artifacts/ibo9k545_LOGO%20CLUB%20CA%CC%81MARA%20%2B%20SIN%20FONDO.png" 
                alt="Club Cámara" 
                className="h-12 w-auto"
              />
            </div>
            <a 
              href="/admin" 
              className="text-sm text-[#64748B] hover:text-[#d1223b] transition-colors"
              data-testid="admin-link"
            >
              Administración
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Hero Text */}
          <div className="text-center mb-8">
            <div className="flex flex-col items-center gap-4 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_619dfbd9-e9ac-4905-ae17-ad2e8903622d/artifacts/tnur302o_Firma%2020%20Aniversario%20%28251%20x%2088%20px%29%20%28350%20x%20100%20px%29%20%281%29%20%282%29%20%281%29.png" 
                alt="Cámara Lanzarote" 
                className="h-16 md:h-20 w-auto"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_619dfbd9-e9ac-4905-ae17-ad2e8903622d/artifacts/ibo9k545_LOGO%20CLUB%20CA%CC%81MARA%20%2B%20SIN%20FONDO.png" 
                alt="Club Cámara" 
                className="h-28 md:h-36 w-auto"
              />
            </div>
            <p className="text-lg text-[#64748B]">
              Beneficios y ventajas exclusivas para las empresas del Club Cámara y sus profesionales.
            </p>
          </div>

          {/* Access Card */}
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#fcd605]/20 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-[#d1223b]" />
                </div>
                <div>
                  <h2 className="font-semibold text-[#0F172A]">Acceso para Empleados</h2>
                  <p className="text-sm text-[#64748B]">Introduce el código de tu empresa</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm" data-testid="error-message">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div>
                  <Input
                    type="text"
                    placeholder="Código de empresa (Ej: ABC2026)"
                    value={code}
                    onChange={handleCodeChange}
                    className="h-14 text-lg text-center font-mono tracking-widest uppercase"
                    maxLength={10}
                    data-testid="access-code-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 btn-primary text-lg"
                  disabled={loading}
                  data-testid="access-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Verificando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Acceder a Ofertas
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                <p className="text-sm text-[#64748B]">
                  ¿No tienes código? Solicítalo a tu departamento de RRHH
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Info Section */}
          <div className="mt-8 flex justify-center gap-8 text-center">
            <div className="p-4">
              <div className="text-2xl font-bold text-[#d1223b]">+20</div>
              <div className="text-sm text-[#64748B]">Empresas asociadas</div>
            </div>
            <div className="p-4">
              <div className="text-2xl font-bold text-[#d1223b]">Gratis</div>
              <div className="text-sm text-[#64748B]">Para empleados</div>
            </div>
          </div>

          {/* Install App Buttons */}
          {!isStandalone && (
            <div className="mt-6 text-center">
              <p className="text-sm text-[#64748B] mb-3">Descarga la app en tu dispositivo</p>
              <div className="flex justify-center gap-3 flex-wrap">
                {/* Android button - always visible on non-standalone */}
                <button
                  onClick={handleInstall}
                  data-testid="install-android-btn"
                  className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-5 py-3 rounded-full font-semibold hover:bg-[#1e293b] transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  Android
                </button>
                {/* iPhone button - always visible on non-standalone */}
                <button
                  onClick={function() { setShowIosModal(true); }}
                  data-testid="install-ios-btn"
                  className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-5 py-3 rounded-full font-semibold hover:bg-[#1e293b] transition-colors text-sm"
                >
                  <Smartphone className="w-4 h-4" />
                  iPhone
                </button>
              </div>
            </div>
          )}

          {/* iOS Install Instructions Modal */}
          {showIosModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={function() { setShowIosModal(false); }}>
              <div className="bg-white rounded-2xl max-w-sm w-full p-6 relative" onClick={function(e) { e.stopPropagation(); }}>
                <button
                  onClick={function() { setShowIosModal(false); }}
                  className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
                  data-testid="close-ios-modal"
                >
                  <X className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-bold text-[#0F172A] mb-4 text-center">Instalar en iPhone</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#d1223b] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                    <p className="text-sm text-[#334155] pt-1">Abre esta página en <strong>Safari</strong></p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#d1223b] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                    <div className="pt-1">
                      <p className="text-sm text-[#334155]">Pulsa el botón <strong>Compartir</strong></p>
                      <Share className="w-5 h-5 text-[#007AFF] mt-1" />
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#d1223b] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                    <p className="text-sm text-[#334155] pt-1">Selecciona <strong>"Añadir a pantalla de inicio"</strong></p>
                  </div>
                </div>
                <button
                  onClick={function() { setShowIosModal(false); }}
                  className="mt-6 w-full bg-[#d1223b] text-white py-2.5 rounded-lg font-semibold hover:bg-[#b91c33] transition-colors"
                >
                  Entendido
                </button>
              </div>
            </div>
          )}

          {/* Android Install Instructions Modal */}
          {showAndroidModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={function() { setShowAndroidModal(false); }}>
              <div className="bg-white rounded-2xl max-w-sm w-full p-6 relative" onClick={function(e) { e.stopPropagation(); }}>
                <button
                  onClick={function() { setShowAndroidModal(false); }}
                  className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
                  data-testid="close-android-modal"
                >
                  <X className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-bold text-[#0F172A] mb-4 text-center">Instalar en Android</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#d1223b] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                    <p className="text-sm text-[#334155] pt-1">Abre esta página en <strong>Chrome</strong></p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#d1223b] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                    <p className="text-sm text-[#334155] pt-1">Pulsa el menú <strong>⋮</strong> (arriba a la derecha)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#d1223b] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                    <p className="text-sm text-[#334155] pt-1">Selecciona <strong>"Añadir a pantalla de inicio"</strong> o <strong>"Instalar app"</strong></p>
                  </div>
                </div>
                <button
                  onClick={function() { setShowAndroidModal(false); }}
                  className="mt-6 w-full bg-[#d1223b] text-white py-2.5 rounded-lg font-semibold hover:bg-[#b91c33] transition-colors"
                >
                  Entendido
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm">
            © 2026 Cámara de Comercio de Lanzarote y La Graciosa
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
